import { css } from '@emotion/core';
import { HORIZONTAL_GLOBAL_NAV_HEIGHT } from '@atlaskit/atlassian-navigation';
import * as colors from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

const DEFAULT_SPACING = 8;

export const alignItemsCenter = css`
  align-items: center;
`;
export const alignItemsStart = css`
  align-items: flex-start;
`;
export const alignItemsEnd = css`
  align-items: flex-end;
`;
export const alignItemsBaseline = css`
  align-items: baseline;
`;

const flexFlowRow = css`
  flex-flow: row wrap;
`;

export const MOBILE_BREAKPOINT = 640;
export const TABLET_BREAKPOINT = 820;
export const DESKTOP_BREAKPOINT = 1400;
export const LARGE_DESKTOP_BREAKPOINT = 1880;
export const EXTRA_LARGE_DESKTOP_BREAKPOINT = 2800;

// TODO: this should be the better breackpoint across the app,
// test globally and consider to replace DESKTOP_BREAKPOINT with this
export const SEARCH_XLARGE_BREAKPOINT = 1440;
export const SEARCH_LARGE_BREAKPOINT = 1366;
export const SEARCH_MEDIUM_BREAKPOINT = 1280;
export const SEARCH_SMALL_BREAKPOINT = 1024;
export const SEARCH_XSMALL_BREAKPOINT = 640;

export const DASHBOARD_XLARGE_BREAKPOINT = 1440;
export const APP_XLARGE_BREAKPOINT = 1280;

export const borderColor = token('color.border', '#eee');

export const BASE_FONT_SIZE = 14;

export const fontFamily = `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu',
    'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;`;

export const fontStack = css`
  font-family: ${fontFamily};
  line-height: 1.4285714285714286;
  height: 2.5714285714285716em;
  font-size: ${BASE_FONT_SIZE}px;
`;

export const center = css`
  justify-content: center;
  ${alignItemsCenter};
`;

const start = css`
  justify-content: flex-start;
  ${alignItemsCenter};
  ${flexFlowRow};
`;

const end = css`
  justify-content: flex-end;
  ${alignItemsCenter};
  ${flexFlowRow};
`;

const space = css`
  justify-content: space-between;
  ${flexFlowRow};
  ${alignItemsCenter};
`;

// 2px
export const SPACE_TINY = DEFAULT_SPACING / 4;
// 4px
export const SPACE_MINIMUM = DEFAULT_SPACING / 2;
// 8px
export const SPACE_COMPACT = DEFAULT_SPACING;
// 16px
export const SPACE_COZY = SPACE_MINIMUM * 4;
// 24px
export const SPACE_COMFORTABLE = SPACE_MINIMUM * 6;
// 32px
export const SPACE_RELAXED = SPACE_MINIMUM * 8;

// 16px
export const DEFAULT_GUTTER = 2 * DEFAULT_SPACING;
export const MIN_FIELD_SIZE = 225;

export const borderTop = css`
  border-top: 1px solid ${borderColor};
`;

export const borderBottom = css`
  border-bottom: 1px solid ${borderColor};
`;

export const borderLeft = css`
  border-left: 1px solid ${borderColor};
`;

export const borderRight = css`
  border-right: 1px solid ${borderColor};
`;

export const borderRadius = css`
  border-radius: ${SPACE_MINIMUM}px;
`;

export const border = css([borderTop, borderBottom]);

export const spaceTopMinimum = css`
  margin-top: ${SPACE_MINIMUM}px;
`;

export const spaceTopCompact = css`
  margin-top: ${SPACE_COMPACT}px;
`;

export const spaceTopCozy = css`
  margin-top: ${SPACE_COZY}px;
`;

export const spaceTopComfortable = css`
  margin-top: ${SPACE_COMFORTABLE}px;
`;

export const spaceTopRelaxed = css`
  margin-top: ${SPACE_RELAXED}px;
`;

export const spaceTopPaddingCompact = css`
  padding-top: ${SPACE_COMPACT}px;
`;

export const spaceGapCompact = css`
  gap: ${SPACE_COMPACT}px;
`;

export const spaceGapTiny = css`
  gap: ${SPACE_TINY}px;
`;

export const spaceGapMinimum = css`
  gap: ${SPACE_MINIMUM}px;
`;

export const spaceGapCozy = css`
  gap: ${SPACE_COZY}px;
`;

export const spaceGapComfortable = css`
  gap: ${SPACE_COMFORTABLE}px;
`;

export const spaceGapRelaxed = css`
  gap: ${SPACE_RELAXED}px;
`;

export const flex = css`
  display: flex;
`;

export const flexDirectionColumn = css`
  flex-direction: column;
`;

export const flexOne = css`
  flex: 1;
`;

export const positionRelative = css`
  position: relative;
`;

export const grid = css`
  display: grid;
  gap: ${DEFAULT_GUTTER * 2}px;
`;

export const gridCompact = css`
  display: grid;
  gap: ${DEFAULT_GUTTER}px;
`;

export const twoColumnGrid = css`
  grid-template-columns: repeat(2, 1fr);
`;

export const threeColumnGrid = css`
  grid-template-columns: repeat(3, 1fr);
`;

export const cursorPointer = css`
  cursor: pointer;
`;

export const marginZero = css`
  margin: 0;
`;

export const paddingZero = css`
  padding: 0;
`;

export const marginAuto = css`
  ${marginZero};
  margin-left: auto;
  margin-right: auto;
`;

export const marginPushRight = css`
  ${marginZero};
  margin-left: auto;
`;

export const maxWidth = css`
  @media screen and (min-width: ${TABLET_BREAKPOINT}px) {
    max-width: 980px;
  }

  @media screen and (min-width: ${DESKTOP_BREAKPOINT}px) {
    max-width: 1280px;
  }
`;

export const maxWidthSearch = css`
  max-width: ${SEARCH_XLARGE_BREAKPOINT}px;
`;

export const maxWidthDashboard = css`
  max-width: ${DASHBOARD_XLARGE_BREAKPOINT}px;
`;

export const maxWidthApp = css`
  max-width: ${APP_XLARGE_BREAKPOINT}px;
`;

export const maxContent = css`
  width: max-content;
`;

export const fadeIn = css`
  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  opacity: 0;
  will-change: opacity;
  animation: fadein 1s ease-out 350ms 1 normal forwards;
`;

export const maxHeight = css`
  height: inherit;
`;

export const maxHeightWithoutNav = css`
  height: calc(100vh - ${HORIZONTAL_GLOBAL_NAV_HEIGHT}px);
`;

const hideWebkitScrollbar = css`
  ::-webkit-scrollbar {
    width: 0px; /* Remove scrollbar space */
    background: transparent; /* Optional: just make scrollbar invisible */
  }
`;

export const overflowXScroll = css([
  css`
    overflow-x: auto;
  `,
  hideWebkitScrollbar,
]);

export const overflowYScroll = css([
  css`
    overflow-y: auto;
  `,
  hideWebkitScrollbar,
]);

export const textOverflowHidden = css`
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

export const textTruncateAt = (textTruncateAtWidth = `180px`) => css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${textTruncateAtWidth};
`;

export const flexCenter = css([flex, center]);
export const flexStart = css([flex, start]);
export const flexSpace = css([flex, space]);
export const flexEnd = css([flex, end]);
export const defaultMargin = css`
  margin: ${DEFAULT_GUTTER}px 0;
`;
export const halfMargin = css`
  margin: ${DEFAULT_GUTTER * 0.5}px 0;
`;

export const holdingBox = css`
  ${flexCenter};
  ${defaultMargin};
`;

export const listReset = css`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const linkStyles = css`
  font-weight: 700;
  color: ${token('color.text', colors.N800)};
`;

/** colors */
export const MEET_COLOR = token('color.background.success.bold', colors.G200);
export const BREACH_COLOR = token('color.background.danger.bold', colors.R200);
export const HISTORICAL_ALERT_COLOR = token('color.background.accent.gray.subtle', colors.R200);
export const HISTORICAL_LIGHT_ALERT_COLOR = token(
  'color.background.accent.gray.subtlest',
  colors.R300
);
export const QUALITY_ISSUE_COLOR = token('color.background.warning.bold', colors.Y300);
export const BURN_COLOR = token('color.background.accent.yellow.subtler.pressed', colors.Y300);
export const BREACH_LIGHT_COLOR = token('color.background.danger', colors.R200);
export const BURN_LIGHT_COLOR = token('color.background.warning', colors.Y300);
export const INCIDENT_COLOR = token('color.background.neutral.bold.pressed', colors.N900);
// Chart palette token, like the low traffic day bar. The legacy palette has no pink, so the
// fallback is the token's light-theme value.
// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const CTD_ERROR_RATE_COLOR = token('color.chart.magenta.bold', '#DA62AC');
export const LOW_TRAFFIC_ALERT_COLOR = token('color.background.discovery.bold', colors.P300);
export const INVERSE_COLOR = token('color.text.inverse', colors.N0);
export const WARNING_COLOR = token('color.text.warning', colors.Y500);

export { colors, layers } from '@atlaskit/theme';

// NOTE: 101, 84, 192 === P300
// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const pulseAnimation = css`
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 white, 0 0 0 rgba(101, 84, 192, 0.1);
    }
    33% {
      box-shadow: 0 0 0 white, 0 0 0px rgba(101, 84, 192, 0.5);
    }
    66%,
    100% {
      box-shadow: 0 0 0 white, 0 0 0 10px rgba(101, 84, 192, 0.01);
    }
  }
`;

/* NOTE:
  The 'faded white bottom' effect, to indicate an overlay-y scroll area.
  Decorates the HelpDialog with overlflow scrollY,
  removes the need for targeting testId prop.
  Example use:

    <AKDialog css={helpDialogWithScroll}>
      <wrapper-div>
        {children}
      </wrapper-div>
    </AKDialog>

  "wrapper-div" is essential and expected from the following code, child nodes following within.
*/
export const helpDialogWithScroll = [
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  css`
    position: relative;
    & > *:nth-of-type(1) {
      max-height: 420px;
      min-height: 68px;
      ${overflowYScroll}

      &:after {
        content: ' ';
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.5035797213622291) 25%,
          rgba(255, 255, 255, 1) 100%
        );
        position: absolute;
        bottom: 0;
        left: 0;
        height: 16px;
        width: 100%;
      }

      & > *:last-child {
        margin-bottom: ${SPACE_COMFORTABLE}px;
      }
    }
  `,
];

export const helpDialogWithInterest = css`
  /* NOTE: Hold up what's with the selector?
  Reason: We don't have control over the underlying AK component.
  This gives us a tighter looking animation. */
  /* TODO: Update this selector to target QuestionCircleIcon instead when converting from InlineMessage. */
  [aria-label='Inline message icon'] {
    &:after {
      content: ' ';
      position: absolute;
      left: 0;
      height: 24px;
      width: 24px;
      ${pulseAnimation}
      border-radius: 50%;
      animation: pulse 2600ms cubic-bezier(0.55, 0.055, 0.675, 0.19) infinite;
    }
  }
`;

/* filters */
export const fadeFilter = css`
  filter: grayscale(100%) contrast(10%) brightness(150%);
`;

/* text */
export const textDecorationNone = css`
  &,
  &:hover {
    text-decoration: none;
  }
`;

export * from './custom';
export * from './constants';

/* size */
export const extendWidth = css`
  width: 100%;
`;

export const zeroWidth = css`
  width: 0;
`;

/* display */
export const displayInlineBlock = css`
  display: inline-block;
`;

export const displayNone = css`
  display: none;
`;

/* align */
export const textAlignCenter = css`
  text-align: center;
`;

/* visibility */
export const visibilityHidden = css`
  visibility: hidden;
`;

/* text */
export const textTransformCapitalize = css`
  text-transform: capitalize;
`;

export const textTransformLowercase = css`
  text-transform: lowercase;
`;

/* prevent overlap */
export const breakWord = css`
  word-break: break-all;
`;

export const whiteSpaceBreakSpaces = css`
  white-space: break-spaces;
`;

/* no line wrap for event table header columns */
export const eventTableStyles = css`
  th span {
    white-space: nowrap;
  }
`;

/* card */
export const card = css`
  border-radius: 3px;
  box-shadow: ${token(
    'elevation.shadow.raised',
    'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.31) 0px 0px 1px'
  )};
  background-color: ${token('elevation.surface', colors.N0)};
`;

/* popup */
export const popup = css`
  position: fixed;
  bottom: ${SPACE_COMFORTABLE}px;
  right: ${SPACE_COMFORTABLE}px;
  z-index: 2;
  a,
  a:hover {
    color: ${token('color.text.inverse', colors.N0)};
    text-decoration: none;
  }
`;

export const popupContentStyles = css`
  padding: ${SPACE_COZY}px;
`;

/* flex misc */
export const flexWrapNoWrap = css`
  flex-wrap: nowrap;
`;
export const flexWrap = css`
  flex-wrap: wrap;
`;

/* inline scroll bar */
const scrollbarWidth = 6;

// Apply this style to any element that needs a scrollbar, and obvisously you
// will also need to apply a max-height to the element
export const scrollStyles = css`
  overflow-y: scroll;
  padding-right: ${SPACE_COMPACT}px;
  margin-top: 2px; /* Space between content scrollbar track and header */

  &::-webkit-scrollbar {
    width: ${scrollbarWidth}px;
  }

  // Use default scrollbar track color (white in most of the browsers)
  // &::-webkit-scrollbar-track {
  //   background: ${token('color.background.neutral', colors.N200A)};
  //   border-radius: ${scrollbarWidth}px;
  // }

  &::-webkit-scrollbar-thumb {
    background: ${token('color.background.neutral.hovered', colors.N300A)};
    border-radius: ${scrollbarWidth}px;
  }
`;

export const scrollbarMarginStyles = css`
  padding-right: ${SPACE_COMPACT + scrollbarWidth}px;
`;

/* expandable table */
export const tableHeadStyles = css`
  border-bottom: 1px solid ${token('color.border.input', colors.N300A)};
  font-size: 12px;
  font-weight: 600;
  color: ${token('color.text.subtlest', colors.N700)};
  padding-bottom: ${SPACE_COMPACT}px;
`;

export const tableScrollableHeadStyles = [
  tableHeadStyles,
  css`
    padding-right: ${SPACE_COMPACT + scrollbarWidth}px;
  `,
];

export const tableRowsStyles = css`
  padding-left: 0;
  margin-top: 0;
  list-style-type: none;
  & > li {
    border-bottom: 1px solid ${token('color.background.neutral.subtle.hovered', colors.N200A)};
    margin-top: 0;
    padding: ${SPACE_COMPACT}px 0;
  }
`;

export const tableRowsCozyPaddingStyles = css`
  & > li {
    padding: ${SPACE_MINIMUM * 3}px 0;
  }
`;

export const tableExpandButtonStyles = css`
  line-height: 1;
  margin-right: 2px; // align with how atlaskit table looks like
`;

export const tableExpandContentStyles = css`
  border: 1px solid ${token('color.border', colors.N200A)};
  background: ${token('color.text.inverse', colors.N0)};
  border-radius: ${SPACE_MINIMUM}px;
`;

export const tableExpandContentInnerStyles = css`
  padding: ${SPACE_COZY}px;
`;

/* tooltip */
export const tooltipStyles = css`
  a {
    color: ${token('color.text.inverse')};
    text-decoration: underline;
  }
`;

export const maxContentTooltip = css`
  max-width: 250px;
  height: 800px;
`;

/* loading bar */
const stripColor = token('color.background.accent.gray.subtle');
const stripBgColor = token('color.background.accent.gray.subtler');
const stripSize = 42;
export const loadingColumnStyles = (opacity: number) => css`
  opacity: ${opacity};
  position: absolute;
  bottom: 0;
  transform-origin: bottom;
  width: 100%;
  height: 25%;
  background-color: ${stripBgColor};
  background-image: linear-gradient(
    -45deg,
    ${stripColor} 25%,
    transparent 25%,
    transparent 50%,
    ${stripColor} 50%,
    ${stripColor} 75%,
    transparent 75%,
    transparent
  );
  background-size: ${stripSize}px ${stripSize}px;
  @keyframes progress-bar-stripes {
    from {
      background-position: ${stripSize}px 0;
    }
    to {
      background-position: 0 0;
    }
  }

  animation: progress-bar-stripes 1500ms linear infinite;
`;

/* Lozenge */

export const LozengeExtendedContainerStyles = css`
  & > span > span {
    max-width: none !important;
  }
`;
